import { useState, useEffect, useCallback, useMemo } from 'react'
import { debounce } from '@/utilities/debounce'
import { GridColDef } from '@mui/x-data-grid'

/**
 * Props for useColumnsStorage hook
 */
export interface UseColumnsStorageProps {
  /**
   * Array of all available columns or column groups
   */
  columns: any[] | Record<string, any[]>
  /**
   * Key for storing selected columns in localStorage
   */
  storageKey: string
  /**
   * Initial columns to select if nothing is stored in localStorage
   */
  initialColumns?: string[]
}

/**
 * Return type for useColumnsStorage hook
 */
export interface UseColumnsStorageReturn {
  /**
   * Currently selected column fields
   */
  selectedColumns: string[]
  /**
   * Default selected column fields from storage or initialization
   */
  defaultSelectedColumns: string[]
  /**
   * Filtered columns based on selection
   */
  filteredColumns: GridColDef[]
  /**
   * Handler for column selection change
   */
  handleChangeColumns: (columns: string[]) => void
  /**
   * Set selected columns directly
   */
  setSelectedColumns: React.Dispatch<React.SetStateAction<string[]>>
}

/**
 * Hook for managing column selection with localStorage persistence
 */
export const useColumnsStorage = ({
  columns,
  storageKey,
  initialColumns
}: UseColumnsStorageProps): UseColumnsStorageReturn => {
  // States for selected and default columns
  const [defaultSelectedColumns, setDefaultSelectedColumns] = useState<
    string[]
  >([])
  const [selectedColumns, setSelectedColumns] = useState<string[]>([])

  // Initialize default selected columns from localStorage or use all columns if not found
  const initDefaultSelectedColumns = useCallback(() => {
    const storedColumns = localStorage.getItem(storageKey)

    if (storedColumns && storedColumns !== '[]') {
      const parsedColumns = JSON.parse(storedColumns)
      setDefaultSelectedColumns(parsedColumns)
      setSelectedColumns(parsedColumns)
    } else {
      // Use initialColumns if provided, otherwise extract from columns
      const defaultColumns =
        initialColumns ||
        (Array.isArray(columns)
          ? columns.map(item => item.field)
          : Object.values(columns)
              .flat()
              .map(item => item.field))

      setDefaultSelectedColumns(defaultColumns)
      setSelectedColumns(defaultColumns)

      // Save to localStorage
      localStorage.setItem(storageKey, JSON.stringify(defaultColumns))
    }
  }, [columns, storageKey, initialColumns])

  // Debounced function to save columns to localStorage
  const saveColumnsToLocalStorage = useMemo(
    () =>
      debounce((columns: string[]) => {
        localStorage.setItem(storageKey, JSON.stringify(columns))
      }, 2000),
    [storageKey]
  )

  // Handle column selection change
  const handleChangeColumns = useCallback(
    (newColumns: string[]) => {
      setSelectedColumns(newColumns)
      saveColumnsToLocalStorage(newColumns)
    },
    [saveColumnsToLocalStorage]
  )

  // Filter available columns based on selected columns
  const filteredColumns = useMemo(() => {
    if (Array.isArray(columns)) {
      return columns.filter(item => selectedColumns.includes(item.field))
    } else {
      // For grouped columns
      return Object.values(columns)
        .flat()
        .filter(item => selectedColumns.includes(item.field))
    }
  }, [columns, selectedColumns])

  // Initialize on mount
  useEffect(() => {
    initDefaultSelectedColumns()
  }, [initDefaultSelectedColumns])

  return {
    selectedColumns,
    defaultSelectedColumns,
    filteredColumns,
    handleChangeColumns,
    setSelectedColumns
  }
}

export default useColumnsStorage
